// src/pages/ArtistDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { getArtistDetails, ArtistDetails } from '../services/artistService';
import { isAuthenticated } from '../services/authService';
import LoadingState from '../components/common/LoadingState';
import ErrorMessage from '../components/common/ErrorMessage';
import AuthPrompt from '../components/auth/AuthPrompt';
import ArtistSearch from '../components/artists/ArtistSearch';
import SongList from '../components/songs/SongList';
import { ArtistNoLinkState } from '../components/artists/ArtistNoLinkState';
import { ArtistMetricsTables } from '../components/artists/ArtistMetricsTables';
import { ArtistKeyMetrics } from '../components/artists/ArtistKeyMetrics';
import { ArtistReleases } from '../components/artists/ArtistReleases';
import MetricsChart from '../components/charts/MetricsChart';
import { getMetricsForYear } from '../utils/metricUtils';
const ArtistDetail: React.FC = () => {
  const { id = 'me' } = useParams<{ id: string }>();
  const isMyProfile = id === 'me';

  const [activeTab, setActiveTab] = useState<'overview' | 'songs'>('overview');
  const [artistData, setArtistData] = useState<ArtistDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArtistSearch, setShowArtistSearch] = useState(false);
  const [noArtistLinked, setNoArtistLinked] = useState(false);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [availableYears, setAvailableYears] = useState<number[]>([currentYear]);

  useEffect(() => {
    const fetchArtistData = async () => {
      if (isMyProfile && !isAuthenticated()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setNoArtistLinked(false);

        const data = await getArtistDetails(id);
        setArtistData(data);

        const uniqueYears = Array.from(
          new Set(
            data.monthlyMetrics?.map(m => {
              const match = m.month.match(/\d{4}/);
              return match ? parseInt(match[0]) : currentYear;
            }) ?? []
          )
        ).sort((a, b) => b - a);

        setAvailableYears(uniqueYears);
        if (uniqueYears.length > 0) setSelectedYear(uniqueYears[0]);

      } catch (err: any) {
        console.error('Error fetching artist data:', err);
        if (err?.response?.status === 404) {
          isMyProfile
            ? setNoArtistLinked(true)
            : setError('Artist not found.');
        } else {
          setError('Failed to load artist data.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArtistData();
  }, [id, isMyProfile, currentYear]);

  const handlePreviousYear = () => {
    const index = availableYears.indexOf(selectedYear);
    if (index < availableYears.length - 1) {
      setSelectedYear(availableYears[index + 1]);
    }
  };

  const handleNextYear = () => {
    const index = availableYears.indexOf(selectedYear);
    if (index > 0) {
      setSelectedYear(availableYears[index - 1]);
    }
  };

  if (isMyProfile && !isAuthenticated()) {
    return (
      <div className="container mx-auto py-8">
        <AuthPrompt
          title="Artist Profile Access"
          message="To view and manage your artist data, you need to sign in with your Spotify account."
        />
      </div>
    );
  }

  if (noArtistLinked) {
    return (
      <ArtistNoLinkState
        showArtistSearch={showArtistSearch}
        setShowArtistSearch={setShowArtistSearch}
      />
    );
  }

  if (loading) return <LoadingState message="Loading artist data..." size="large" />;
  if (error || !artistData) {
    return (
      <ErrorMessage
        title="Failed to load artist data"
        message={error || "Could not load artist information."}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const { artist, releases, songs, monthlyMetrics } = artistData;
  console.log('Monthly metrics:', monthlyMetrics);
console.log('Selected year:', selectedYear);
console.log('Filtered metrics:', getMetricsForYear(monthlyMetrics || [], selectedYear));
  const metricsWithChanges = getMetricsForYear(monthlyMetrics || [], selectedYear);

  
  const latestMetric = metricsWithChanges.at(-1) || null;
  const previousMetric = metricsWithChanges.length > 1 ? metricsWithChanges.at(-2) : null;

  const currentScore = latestMetric ? latestMetric.score : artist.score;
  const previousScore = previousMetric ? previousMetric.score : artist.previousScore;
  const scoreChange = +(currentScore - previousScore).toFixed(2);
  const isScorePositive = scoreChange > 0;

  return (
    <div className="container mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{artist.name}</h1>
          <p className="text-gray-600">{artist.genre} • Score: {artist.score}/100</p>
        </div>

        <div className="flex items-center bg-gray-50 border border-gray-200 rounded-md p-2">
          <button
            onClick={handlePreviousYear}
            className="p-1 rounded-full hover:bg-gray-200"
            disabled={availableYears.indexOf(selectedYear) >= availableYears.length - 1}
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex items-center mx-2">
            <CalendarIcon className="h-5 w-5 text-gray-500 mr-2" />
            <span className="text-gray-700 font-medium">{selectedYear}</span>
          </div>
          <button
            onClick={handleNextYear}
            className="p-1 rounded-full hover:bg-gray-200"
            disabled={availableYears.indexOf(selectedYear) <= 0}
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`${
              activeTab === 'overview'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('songs')}
            className={`${
              activeTab === 'songs'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Songs ({songs.length})
          </button>
        </nav>
      </div>

      {activeTab === 'overview' && (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-1">
      <div className="h-96 mb-6">
        <ArtistKeyMetrics
          artist={artist}
          latestMetric={latestMetric}
          scoreChange={scoreChange}
          isScorePositive={isScorePositive}
        />
      </div>
      <ArtistReleases releases={releases} />
    </div>

    <div className="lg:col-span-2 space-y-6">
      <div className="h-96">
        <MetricsChart 
          metrics={metricsWithChanges} 
          height="calc(100% - 20px)"
        />
      </div>
      <ArtistMetricsTables
        metrics={metricsWithChanges}
        selectedYear={selectedYear}
      />
    </div>
  </div>
)}

      {activeTab === 'songs' && (
        <div className="bg-white rounded-lg shadow p-6">
          <SongList songs={songs} artistId={id || ''} />
        </div>
      )}
    </div>
  );
};

export default ArtistDetail;
